/**
 * Template Engine types — JSDoc typedefs plus empty-value factories.
 * Plain JavaScript, no TypeScript: every shape below is a comment, and
 * every factory returns the empty-but-fully-shaped value so consumers
 * never have to guard on missing keys.
 */

/**
 * What a parser receives. Never a raw File, never a raw string — see
 * normalizeInput. `text` is always populated; binary formats extend this
 * shape with `arrayBuffer` rather than changing `text`.
 *
 * @typedef {Object} Input
 * @property {'file'|'text'} kind
 * @property {string} text
 * @property {string} filename    '' for pasted text
 * @property {string} mimeType    '' for pasted text
 * @property {number} byteLength
 */

/**
 * Accepted now even though Phase 1 has no worker and nothing long enough to
 * cancel: retrofitting cancellation and progress once five parsers exist would
 * mean touching all five.
 *
 * `onProgress` reports a CUMULATIVE pipeline fraction, not a stage-local one.
 * The importer reserves 0.2–0.5 for the parse stage; a parser reporting raw
 * 0→1 would drive a progress bar to full and then back down.
 *
 * @typedef {Object} Context
 * @property {AbortSignal} signal
 * @property {(stage: string, fraction: number) => void} onProgress
 * @property {Object} metadata    caller-supplied, merged into the result
 */

/**
 * What a parser returns — only what the parser alone can know. Variables,
 * metadata, preview, and content hash are derived identically for every
 * format, so the importer owns them; a parser computing them would be one of
 * six implementations slowly drifting apart.
 *
 * @typedef {Object} ParserOutput
 * @property {Object} document
 * @property {Assets} assets
 * @property {Report} report
 */

/**
 * A parser is a plain object, not a class instance. No base class, nothing to
 * keep in sync as formats are added.
 *
 * @typedef {Object} Parser
 * @property {string} id        unique, stable; recorded in metadata.parser
 * @property {string} label     shown in the UI
 * @property {number} priority  higher wins; ties resolve by registration order
 * @property {string[]} accept  extensions and mime types for the file input
 * @property {(input: Input) => boolean} canParse   cheap; must not throw
 * @property {(input: Input, context: Context) => Promise<ParserOutput>} parse
 */

/**
 * @typedef {Object} Assets
 * @property {Array} fonts
 * @property {Array} images
 * @property {Array} icons
 * @property {Array} patterns
 * @property {Array} frames
 */

/** @returns {Assets} */
export const emptyAssets = () => ({
  fonts: [],
  images: [],
  icons: [],
  patterns: [],
  frames: [],
});

/**
 * @typedef {Object} Report
 * @property {Array} supported
 * @property {Array} partial
 * @property {Array} unsupported
 * @property {Array} warnings
 * @property {Array} errors
 */

/** @returns {Report} */
export const emptyReport = () => ({
  supported: [],
  partial: [],
  unsupported: [],
  warnings: [],
  errors: [],
});

/**
 * @typedef {Object} PreviewPage
 * @property {string} id
 * @property {string} name
 * @property {string} background
 * @property {number} elementCount
 */

/**
 * @typedef {Object} Preview
 * @property {{width: number, height: number}} canvas
 * @property {number} pageCount
 * @property {PreviewPage[]} pages
 */

/** @returns {Preview} */
export const emptyPreview = () => ({
  canvas: { width: 0, height: 0 },
  pageCount: 0,
  pages: [],
});

/**
 * A detected {{TOKEN}} placeholder. `elementIds` is carried so a later bulk
 * replacement pass does not have to re-walk the document to find them.
 *
 * @typedef {Object} Variable
 * @property {string} token          as written, without braces
 * @property {string} name           human label
 * @property {'text'|'image'} kind
 * @property {string[]} elementIds
 * @property {string} defaultValue   the literal {{TOKEN}}
 */

/**
 * Provenance of an imported document.
 *
 * @typedef {Object} ImportMetadata
 * @property {string|null} parser
 * @property {number|null} parser_version
 * @property {string} source
 * @property {number} import_version
 * @property {string|null} library_id
 * @property {string|null} content_hash   null when crypto.subtle is unavailable
 * @property {string} imported_at
 */

/**
 * What TemplateImporter returns. Every key is always present — arrays are
 * empty rather than absent — so no consumer has to guard.
 *
 * @typedef {Object} ParseResult
 * @property {Object} document
 * @property {Assets} assets
 * @property {Variable[]} variables
 * @property {ImportMetadata} metadata
 * @property {Preview} preview
 * @property {Report} report
 */
